import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import axios from 'axios';
import StripePayment from './StripePayment';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const GetStarted = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [availableDates, setAvailableDates] = useState([]);
  const [loadingDates, setLoadingDates] = useState(false);
  const [formData, setFormData] = useState({
    // Step 1: Account Setup
    fullName: '',
    email: '',
    phone: '',
    password: '',
    city: '',
    timezone: '',
    communicationPreference: 'email',
    
    // Step 2: Company Info
    companyName: '',
    industry: '',
    currentRevenue: '',
    website: '',
    socialLinks: '',
    oneLiner: '',
    foundingStage: '',
    challenges: [],
    goals: '',
    
    // Step 3: Scheduling
    kickoffDate: null,
    kickoffTime: null,
    
    // Step 4: Payment Plan
    selectedPlan: '',
    
    // Step 5: Payment
    paymentStatus: 'pending'
  });
  
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const totalSteps = 5;
  const progressPercentage = (currentStep / totalSteps) * 100;

  // Fetch available dates when reaching step 3
  useEffect(() => {
    if (currentStep === 3 && availableDates.length === 0) {
      fetchAvailableDates();
    }
  }, [currentStep]);

  const fetchAvailableDates = async () => {
    setLoadingDates(true);
    try {
      const response = await axios.get(`${API}/booking/available-dates`);
      setAvailableDates(response.data.data.available_dates);
    } catch (error) {
      console.error('Failed to fetch available dates:', error);
      // Fallback to mock dates if API fails
      setAvailableDates([
        { date: '2024-12-15', display: 'Dec 15', day_of_week: 'Sunday', available_times: ['10:00 AM', '2:00 PM'] },
        { date: '2024-12-18', display: 'Dec 18', day_of_week: 'Wednesday', available_times: ['9:00 AM', '1:00 PM'] },
        { date: '2024-12-22', display: 'Dec 22', day_of_week: 'Sunday', available_times: ['10:00 AM', '3:00 PM'] },
        { date: '2025-01-03', display: 'Jan 3', day_of_week: 'Friday', available_times: ['11:00 AM', '3:00 PM'] },
        { date: '2025-01-08', display: 'Jan 8', day_of_week: 'Wednesday', available_times: ['9:00 AM', '2:00 PM'] },
        { date: '2025-01-12', display: 'Jan 12', day_of_week: 'Sunday', available_times: ['10:00 AM', '4:00 PM'] }
      ]);
    } finally {
      setLoadingDates(false);
    }
  };

  const scheduleKickoff = async (dateData, timeSlot) => {
    try {
      const response = await axios.post(`${API}/booking/schedule-kickoff`, {
        customer_email: formData.email,
        customer_details: {
          first_name: formData.fullName.split(' ')[0],
          last_name: formData.fullName.split(' ').slice(1).join(' '),
          company: formData.companyName,
          phone: formData.phone,
          scheduled_time: `${dateData.date}T${timeSlot}`
        }
      });
      
      console.log('Kickoff scheduled:', response.data);
      updateFormData('kickoffDate', `${dateData.display} at ${timeSlot}`);
    } catch (error) {
      console.error('Failed to schedule kickoff:', error);
      setErrors({ kickoff: 'Failed to schedule kickoff. Please try again.' });
    }
  };

  // Auto-save progress
  useEffect(() => {
    const saved = localStorage.getItem('onboardingData');
    if (saved) {
      setFormData(JSON.parse(saved));
    }
  }, []);

  useEffect(() => {
    localStorage.setItem('onboardingData', JSON.stringify(formData));
  }, [formData]);

  const updateFormData = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  const validateStep = (step) => {
    const newErrors = {};
    
    switch (step) {
      case 1:
        if (!formData.fullName) newErrors.fullName = 'Full name is required';
        if (!formData.email) newErrors.email = 'Email is required';
        if (!formData.password || formData.password.length < 6) newErrors.password = 'Password must be at least 6 characters';
        break;
      case 2:
        if (!formData.companyName) newErrors.companyName = 'Company name is required';
        if (!formData.industry) newErrors.industry = 'Industry is required';
        if (!formData.oneLiner) newErrors.oneLiner = 'Company description is required';
        break;
      case 4:
        if (!formData.selectedPlan) newErrors.selectedPlan = 'Please select a payment plan';
        break;
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const nextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, totalSteps));
    }
  };

  const prevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const handleSubmit = async (step) => {
    if (step === 1) {
      // Create account
      setLoading(true);
      try {
        await axios.post(`${API}/leads/capture`, {
          email: formData.email,
          first_name: formData.fullName.split(' ')[0],
          last_name: formData.fullName.split(' ').slice(1).join(' '),
          phone: formData.phone,
          source: 'onboarding'
        });
        nextStep();
      } catch (error) {
        setErrors({ general: 'Failed to create account. Please try again.' });
      } finally {
        setLoading(false);
      }
    } else if (step === 5) {
      // Process payment
      handlePayment();
    } else {
      nextStep();
    }
  };

  const handlePaymentSuccess = async (paymentData) => {
    console.log('Payment successful:', paymentData);
    updateFormData('paymentStatus', 'completed');
    
    // Send enrollment completion notification
    try {
      const enrollmentData = {
        // Personal Information
        first_name: formData.fullName.split(' ')[0] || '',
        last_name: formData.fullName.split(' ').slice(1).join(' ') || '',
        email: formData.email,
        phone: formData.phone,
        
        // Company Information
        company_name: formData.companyName,
        current_revenue: formData.currentRevenue || 'Not specified',
        team_size: formData.teamSize || 'Not specified',
        founding_stage: formData.foundingStage || 'Not specified',
        one_liner: formData.oneLiner || 'Not provided',
        
        // Business Details
        biggest_challenge: formData.challenges?.length > 0 ? formData.challenges.join(', ') : 'Not specified',
        goals: formData.goals || 'Not specified',
        success_metrics: 'Not specified', // This field might not be in the current form
        challenges: formData.challenges || [],
        
        // Program Selection
        selected_plan: formData.selectedPlan,
        kickoff_date: formData.kickoffDate,
        kickoff_time: formData.kickoffTime,
        
        // Payment Status
        payment_status: 'completed',
        payment_data: paymentData
      };
      
      console.log('Sending enrollment completion notification...', enrollmentData);
      
      const response = await axios.post(`${API}/leads/enrollment-complete`, enrollmentData);
      console.log('Enrollment notification sent:', response.data);
      
    } catch (error) {
      console.error('Failed to send enrollment notification:', error);
      // Don't block the success flow if notification fails
    }
    
    // Clear saved data
    localStorage.removeItem('onboardingData');
    
    // Show success message
    alert('🎉 Payment processed successfully! Welcome to Advance by Copper & Vine. Check your email for next steps and your kickoff session details.');
  };

  const handlePaymentError = (errorMessage) => {
    console.error('Payment failed:', errorMessage);
    setErrors({ payment: errorMessage });
  };

  const handlePayment = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/payments/create-intent`, {
        plan_type: formData.selectedPlan,
        customer_email: formData.email,
        customer_details: {
          first_name: formData.fullName.split(' ')[0],
          last_name: formData.fullName.split(' ').slice(1).join(' '),
          company: formData.companyName,
          phone: formData.phone
        }
      });
      
      // In a real implementation, you would redirect to Stripe
      console.log('Payment intent created:', response.data);
      updateFormData('paymentStatus', 'completed');
      
      // Clear saved data
      localStorage.removeItem('onboardingData');
      
      // Show success message
      alert('Payment processed successfully! Check your email for next steps.');
      
    } catch (error) {
      setErrors({ payment: 'Payment failed. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const challengeOptions = [
    'Brand identity and messaging',
    'Systems and automation',
    'Offer structure and pricing',
    'Growth and marketing',
    'AI and technology',
    'Time management and operations'
  ];

  const paymentPlans = [
    {
      id: 'investment',
      title: '🚀 Investment Build — Best Value',
      price: '$22,500 (One-Time Payment)',
      savings: 'Save $2,500 compared to other plans',
      description: 'Complete 10-week Accelerator',
      features: [
        'Priority Start: Begin in 7 days (vs. 14)',
        'Extended ROI: +3 months AI Co-Pilot hosting', 
        'Executive Edge: Investor deck polish + pitch coaching'
      ],
      tagline: 'For leaders who want maximum momentum and immediate ROI.',
      highlight: true
    },
    {
      id: 'milestone',
      title: '⚡ Milestone Build — Balanced',
      price: '2 Payments of $12,500 (Total $25,000)',
      description: 'Aligned to key project milestones',
      features: [
        'Complete 10-week Accelerator',
        '50% due at kickoff, 50% due at Week 6',
        'Strategic Bonus: Professional brand photography kit'
      ],
      tagline: 'For executives who prefer milestone-based capital deployment.'
    },
    {
      id: 'installment',
      title: '📈 Installment Build — Flexible',
      price: '4 Payments of $7,000 (Total $28,000)',
      description: 'Distributed across 10 weeks',
      features: [
        'Complete 10-week Accelerator',
        'Lower entry payments, spread out',
        'Strategic Bonus: Post-engagement strategy office hours'
      ],
      tagline: 'For owners balancing cash flow while still securing the full build.'
    },
    {
      id: 'diagnostic',
      title: '⏳ Strategic Diagnostic — Starter Path',
      price: '$5,000 Flat Fee',
      description: 'Covers Weeks 1–3 only (Vision, Business Model, Customer Journey)',
      features: [
        'Tangible strategic deliverables',
        'Pauses until remaining $20,000 is invested',
        'Credit: $5,000 fully applied if you continue'
      ],
      tagline: 'For founders validating fit or preparing for outside funding.'
    }
  ];

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <h2 className="headline-medium text-charcoal mb-4">Account Setup & Contact Info</h2>
              <p className="body-medium text-graphite">Create your Accelerator profile and connect to your portal</p>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="label-text text-charcoal block mb-2">FULL NAME *</label>
                <input
                  type="text"
                  value={formData.fullName}
                  onChange={(e) => updateFormData('fullName', e.target.value)}
                  className={`w-full p-4 border rounded-lg focus:outline-none focus:border-bronze-strong ${
                    errors.fullName ? 'border-red-500' : 'border-bronze-light'
                  }`}
                  placeholder="John Smith"
                />
                {errors.fullName && <p className="text-red-500 text-sm mt-1">{errors.fullName}</p>}
              </div>

              <div>
                <label className="label-text text-charcoal block mb-2">EMAIL *</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => updateFormData('email', e.target.value)}
                  className={`w-full p-4 border rounded-lg focus:outline-none focus:border-bronze-strong ${
                    errors.email ? 'border-red-500' : 'border-bronze-light'
                  }`}
                  placeholder="john@company.com"
                />
                {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
              </div>

              <div>
                <label className="label-text text-charcoal block mb-2">PHONE (OPTIONAL)</label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => updateFormData('phone', e.target.value)}
                  className="w-full p-4 border border-bronze-light rounded-lg focus:outline-none focus:border-bronze-strong"
                  placeholder="+1 (555) 123-4567"
                />
              </div>

              <div>
                <label className="label-text text-charcoal block mb-2">PASSWORD *</label>
                <input
                  type="password"
                  value={formData.password}
                  onChange={(e) => updateFormData('password', e.target.value)}
                  className={`w-full p-4 border rounded-lg focus:outline-none focus:border-bronze-strong ${
                    errors.password ? 'border-red-500' : 'border-bronze-light'
                  }`}
                  placeholder="Minimum 6 characters"
                />
                {errors.password && <p className="text-red-500 text-sm mt-1">{errors.password}</p>}
              </div>

              <div>
                <label className="label-text text-charcoal block mb-2">CITY, STATE</label>
                <input
                  type="text"
                  value={formData.city}
                  onChange={(e) => updateFormData('city', e.target.value)}
                  className="w-full p-4 border border-bronze-light rounded-lg focus:outline-none focus:border-bronze-strong"
                  placeholder="Los Angeles, CA"
                />
              </div>

              <div>
                <label className="label-text text-charcoal block mb-2">COMMUNICATION PREFERENCE</label>
                <select
                  value={formData.communicationPreference}
                  onChange={(e) => updateFormData('communicationPreference', e.target.value)}
                  className="w-full p-4 border border-bronze-light rounded-lg focus:outline-none focus:border-bronze-strong"
                >
                  <option value="email">Email only</option>
                  <option value="text">Text/SMS only</option>
                  <option value="both">Both email and text</option>
                </select>
              </div>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <h2 className="headline-medium text-charcoal mb-4">Company Idea & Business Snapshot</h2>
              <p className="body-medium text-graphite">Help us understand your business for strategy call prep</p>
            </div>

            <div className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="label-text text-charcoal block mb-2">COMPANY NAME *</label>
                  <input
                    type="text"
                    value={formData.companyName}
                    onChange={(e) => updateFormData('companyName', e.target.value)}
                    className={`w-full p-4 border rounded-lg focus:outline-none focus:border-bronze-strong ${
                      errors.companyName ? 'border-red-500' : 'border-bronze-light'
                    }`}
                    placeholder="Your Company Inc."
                  />
                  {errors.companyName && <p className="text-red-500 text-sm mt-1">{errors.companyName}</p>}
                </div>

                <div>
                  <label className="label-text text-charcoal block mb-2">INDUSTRY/NICHE *</label>
                  <input
                    type="text"
                    value={formData.industry}
                    onChange={(e) => updateFormData('industry', e.target.value)}
                    className={`w-full p-4 border rounded-lg focus:outline-none focus:border-bronze-strong ${
                      errors.industry ? 'border-red-500' : 'border-bronze-light'
                    }`}
                    placeholder="SaaS, Consulting, E-commerce, etc."
                  />
                  {errors.industry && <p className="text-red-500 text-sm mt-1">{errors.industry}</p>}
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="label-text text-charcoal block mb-2">CURRENT REVENUE</label>
                  <select
                    value={formData.currentRevenue}
                    onChange={(e) => updateFormData('currentRevenue', e.target.value)}
                    className="w-full p-4 border border-bronze-light rounded-lg focus:outline-none focus:border-bronze-strong"
                  >
                    <option value="">Select revenue range</option>
                    <option value="pre-revenue">Pre-revenue</option>
                    <option value="under-50k">&lt;$50K</option>
                    <option value="50k-250k">$50K–$250K</option>
                    <option value="over-250k">$250K+</option>
                  </select>
                </div>

                <div>
                  <label className="label-text text-charcoal block mb-2">FOUNDING STAGE</label>
                  <select
                    value={formData.foundingStage}
                    onChange={(e) => updateFormData('foundingStage', e.target.value)}
                    className="w-full p-4 border border-bronze-light rounded-lg focus:outline-none focus:border-bronze-strong"
                  >
                    <option value="">Select stage</option>
                    <option value="idea">Idea</option>
                    <option value="mvp">MVP</option>
                    <option value="early-revenue">Early Revenue</option>
                    <option value="scaling">Scaling</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="label-text text-charcoal block mb-2">WEBSITE / SOCIAL LINKS</label>
                <input
                  type="url"
                  value={formData.website}
                  onChange={(e) => updateFormData('website', e.target.value)}
                  className="w-full p-4 border border-bronze-light rounded-lg focus:outline-none focus:border-bronze-strong"
                  placeholder="https://yourcompany.com"
                />
              </div>

              <div>
                <label className="label-text text-charcoal block mb-2">ONE-LINER: WHAT DOES YOUR COMPANY DO? *</label>
                <textarea
                  value={formData.oneLiner}
                  onChange={(e) => updateFormData('oneLiner', e.target.value)}
                  className={`w-full p-4 border rounded-lg focus:outline-none focus:border-bronze-strong ${
                    errors.oneLiner ? 'border-red-500' : 'border-bronze-light'
                  }`}
                  rows="3"
                  placeholder="We help [target audience] achieve [outcome] through [solution/method]..."
                />
                {errors.oneLiner && <p className="text-red-500 text-sm mt-1">{errors.oneLiner}</p>}
              </div>

              <div>
                <label className="label-text text-charcoal block mb-4">BIGGEST CURRENT CHALLENGES (SELECT ALL THAT APPLY)</label>
                <div className="grid md:grid-cols-2 gap-3">
                  {challengeOptions.map((challenge, index) => (
                    <label key={index} className="flex items-center space-x-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.challenges.includes(challenge)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            updateFormData('challenges', [...formData.challenges, challenge]);
                          } else {
                            updateFormData('challenges', formData.challenges.filter(c => c !== challenge));
                          }
                        }}
                        className="w-4 h-4 text-bronze-strong"
                      />
                      <span className="body-small text-charcoal">{challenge}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label className="label-text text-charcoal block mb-2">WHAT DO YOU HOPE TO ACHIEVE IN 10 WEEKS?</label>
                <textarea
                  value={formData.goals}
                  onChange={(e) => updateFormData('goals', e.target.value)}
                  className="w-full p-4 border border-bronze-light rounded-lg focus:outline-none focus:border-bronze-strong"
                  rows="4"
                  placeholder="Describe your goals, vision, and desired outcomes..."
                />
              </div>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <h2 className="headline-medium text-charcoal mb-4">Schedule Your Kickoff</h2>
              <p className="body-medium text-graphite">Book your 90-minute strategy session and lock in your build window</p>
            </div>

            <Card className="editorial-card p-8">
              <div className="space-y-6">
                <div className="text-center">
                  <div className="text-6xl mb-4">📅</div>
                  <h3 className="subhead-medium text-charcoal">Choose Your Kickoff Date & Time</h3>
                  <p className="body-medium text-graphite mb-6">
                    Your kickoff session is scheduled with our team to prepare your custom build plan.
                  </p>
                </div>
                
                {loadingDates ? (
                  <div className="text-center py-8">
                    <div className="text-bronze text-lg">Loading available dates...</div>
                  </div>
                ) : (
                  <div className="space-y-6">
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {availableDates.map((dateData, index) => (
                        <Card key={index} className="p-4 hover-lift cursor-pointer border border-bronze-light">
                          <div className="text-center mb-3">
                            <div className="text-lg font-semibold text-charcoal">{dateData.display}</div>
                            <div className="text-sm text-graphite">{dateData.day_of_week}</div>
                          </div>
                          <div className="space-y-2">
                            {dateData.available_times.map((time, timeIndex) => (
                              <button
                                key={timeIndex}
                                onClick={() => {
                                  scheduleKickoff(dateData, time);
                                }}
                                className={`w-full p-2 rounded border text-sm transition-colors ${
                                  formData.kickoffDate?.includes(dateData.display) && formData.kickoffDate?.includes(time)
                                    ? 'bg-bronze text-white border-bronze'
                                    : 'bg-white border-bronze-light hover:border-bronze hover:bg-bronze hover:text-white'
                                }`}
                              >
                                {time}
                              </button>
                            ))}
                          </div>
                        </Card>
                      ))}
                    </div>

                    {formData.kickoffDate && (
                      <div className="bg-teal bg-opacity-10 p-6 rounded-lg text-center">
                        <div className="text-teal text-lg mb-2">✓ Kickoff Scheduled</div>
                        <p className="body-medium text-charcoal">
                          <strong>{formData.kickoffDate}</strong>
                        </p>
                        <p className="body-small text-graphite mt-2">
                          You'll receive a calendar invite and confirmation email shortly. Our team will prepare your custom build plan before the session.
                        </p>
                      </div>
                    )}

                    {errors.kickoff && (
                      <div className="bg-red-50 p-4 rounded-lg text-center">
                        <p className="text-red-600">{errors.kickoff}</p>
                      </div>
                    )}

                    <div className="text-center">
                      <p className="body-small text-graphite">
                        <strong>What to expect:</strong> 90-minute strategy session with your dedicated build team to review your business, clarify goals, and create your custom 10-week roadmap.
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </Card>
          </div>
        );

      case 4:
        return (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <h2 className="headline-medium text-charcoal mb-4">Select Your Payment Plan</h2>
              <p className="body-medium text-graphite">Choose the investment path that fits your pace</p>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {paymentPlans.map((plan) => (
                <Card 
                  key={plan.id}
                  className={`editorial-card cursor-pointer transition-all duration-300 ${
                    formData.selectedPlan === plan.id
                      ? 'ring-2 ring-bronze bg-bronze text-white'
                      : plan.highlight
                      ? 'border-bronze-strong'
                      : 'hover:border-bronze'
                  }`}
                  onClick={() => updateFormData('selectedPlan', plan.id)}
                >
                  <div className="p-6">
                    <div className="text-center mb-4">
                      <div className="text-3xl mb-3">{plan.title.split(' ')[0]}</div>
                      <h3 className={`subhead-medium mb-3 ${
                        formData.selectedPlan === plan.id ? 'text-white' : 'text-charcoal'
                      }`}>
                        {plan.title.substring(2)}
                      </h3>
                      <div className={`text-xl font-bold mb-2 ${
                        formData.selectedPlan === plan.id ? 'text-white' : 'text-bronze'
                      }`}>
                        {plan.price}
                      </div>
                      {plan.savings && (
                        <div className={`text-sm mb-3 ${
                          formData.selectedPlan === plan.id ? 'text-white opacity-90' : 'text-teal'
                        }`}>
                          {plan.savings}
                        </div>
                      )}
                      <p className={`body-small mb-4 ${
                        formData.selectedPlan === plan.id ? 'text-white opacity-90' : 'text-graphite'
                      }`}>
                        {plan.description}
                      </p>
                    </div>

                    <div className="text-left mb-4">
                      <ul className="space-y-2">
                        {plan.features?.map((feature, idx) => (
                          <li key={idx} className={`body-small flex items-start ${
                            formData.selectedPlan === plan.id ? 'text-white opacity-90' : 'text-charcoal'
                          }`}>
                            <span className={`mr-2 ${
                              formData.selectedPlan === plan.id ? 'text-white' : 'text-bronze'
                            }`}>•</span>
                            {feature}
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div className={`p-3 rounded-lg text-center ${
                      formData.selectedPlan === plan.id ? 'bg-white bg-opacity-20' : 'bg-ivory'
                    }`}>
                      <p className={`body-small italic ${
                        formData.selectedPlan === plan.id ? 'text-white' : 'text-charcoal'
                      }`}>
                        👉 {plan.tagline}
                      </p>
                    </div>
                  </div>
                </Card>
              ))}
            </div>

            {errors.selectedPlan && (
              <p className="text-red-500 text-center mt-4">{errors.selectedPlan}</p>
            )}
          </div>
        );

      case 5:
        return (
          <StripePayment 
            formData={formData}
            onPaymentSuccess={handlePaymentSuccess}
            onPaymentError={handlePaymentError}
            loading={loading}
            setLoading={setLoading}
          />
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-ivory">
      <div className="container mx-auto px-6 py-12">
        <div className="max-w-4xl mx-auto">
          {/* Progress Bar */}
          <div className="mb-12">
            <div className="flex items-center justify-between mb-4">
              <h1 className="headline-small text-charcoal">Get Started</h1>
              <span className="body-small text-graphite">
                Total Time: ~5–7 min
              </span>
            </div>
            
            <div className="w-full bg-gray-200 rounded-full h-2 mb-4">
              <div 
                className="bg-bronze h-2 rounded-full transition-all duration-300 ease-out"
                style={{ width: `${progressPercentage}%` }}
              ></div>
            </div>
            
            <div className="flex justify-between text-xs text-graphite">
              <span className={currentStep >= 1 ? 'text-bronze font-semibold' : ''}>Account</span>
              <span className={currentStep >= 2 ? 'text-bronze font-semibold' : ''}>Company</span>
              <span className={currentStep >= 3 ? 'text-bronze font-semibold' : ''}>Schedule</span>
              <span className={currentStep >= 4 ? 'text-bronze font-semibold' : ''}>Payment Plan</span>
              <span className={currentStep >= 5 ? 'text-bronze font-semibold' : ''}>Complete</span>
            </div>
          </div>

          {/* Step Content */}
          <Card className="editorial-card p-8 mb-8">
            {renderStep()}
          </Card>

          {/* Navigation */}
          <div className="flex justify-between">
            <Button
              variant="outline"
              className="btn-secondary"
              onClick={prevStep}
              disabled={currentStep === 1}
            >
              Previous
            </Button>

            <div className="text-center">
              <p className="body-small text-graphite mb-2">
                We're excited to build with you!
              </p>
              <p className="label-text text-bronze">
                Step {currentStep} of {totalSteps}
              </p>
            </div>

            <Button
              className="btn-primary"
              onClick={() => handleSubmit(currentStep)}
              disabled={loading}
            >
              {loading 
                ? 'Processing...' 
                : currentStep === totalSteps 
                ? 'Complete Payment' 
                : 'Continue'
              }
            </Button>
          </div>

          {errors.general && (
            <p className="text-red-500 text-center mt-4">{errors.general}</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default GetStarted;